import MapBaseOperate from '@/utils/olMapOperate';
import { Map as OlMap, Feature as OlFeature, Overlay } from 'ol';
import { unByKey } from 'ol/Observable';
import type { EventsKey } from 'ol/events';
import { MapLayerEnum, TravelStatus } from '@/utils/enum';
import Icon from 'ol/style/Icon.js';
import { Style } from 'ol/style';
import Stroke from 'ol/style/Stroke.js';
import MultiLineString from 'ol/geom/MultiLineString.js';
import LineString from 'ol/geom/LineString';
import Point from 'ol/geom/Point.js';
import * as turf from '@turf/turf';

/**
 * 坐标转换工具函数
 * @param coordinates 坐标数组 [longitude, latitude]
 * @returns 转换后的坐标数组
 */
export const transformLocationWgs84toGcj02 = (coordinates: any[]) => {
  const win = window as any;
  const formatedLoaction = win
    .coordtransform()
    .wgs84togcj02(parseFloat(coordinates[0]), parseFloat(coordinates[1]));
  return [
    coordinates[0] ? formatedLoaction[0] : null,
    coordinates[1] ? formatedLoaction[1] : null,
  ];
};

/**
 * 统一的地图操作类，整合了MapRoutesOperate、MapRoutesOperateWithCustomRouting和NavigationOperate的功能
 */
class MapOperations extends MapBaseOperate {
  // 实时位置相关
  realTimePositionFeature: any;
  vehiclePositionFeature: any;

  // 起点终点相关
  startPointFeature: any;
  endPointFeature: any;

  // 路径相关
  travelPathFeature: any;
  scheduledPathFeature: any;
  finishedPathFeature: any;
  routingPathFeature: any;
  customRoutingFeature: any;
  taskRouteFeature: any;

  // 其他标记
  poiMarkers: any[];
  fourLaneFeatures: any[];
  specialMarkers: any[];
  specialOverlays: any[];

  // 箭头相关
  scheduledPathArrows: OlFeature[] = [];
  finishedPathArrows: OlFeature[] = [];

  // 车辆信息气泡overlay
  vehicleInfoOverlay: Overlay | null = null;

  // 停靠点和Home点的overlays
  stopPointOverlays: Overlay[] = [];

  /** 地图缩放事件的key，用于在 clearAll 时移除 */
  private _resolutionListenerKey: EventsKey | null = null;
  /** 特殊标记的点击事件key，用于 clearSpecialMarkers 时移除 */
  private _specialMarkerClickKeys: EventsKey[] = [];

  constructor(map: OlMap) {
    super(map);

    // 实时位置相关
    this.realTimePositionFeature = null;
    this.vehiclePositionFeature = null;

    // 起点终点相关
    this.startPointFeature = null;
    this.endPointFeature = null;

    // 路径相关
    this.travelPathFeature = null;
    this.scheduledPathFeature = null;
    this.finishedPathFeature = null;
    this.routingPathFeature = null;
    this.customRoutingFeature = null;
    this.taskRouteFeature = null;

    // 其他标记
    this.poiMarkers = [];
    this.fourLaneFeatures = [];
    this.specialMarkers = [];
    this.specialOverlays = [];

    // 监听地图缩放变化事件（保存 key 以便 clearAll 时移除）
    this._resolutionListenerKey = this.map
      .getView()
      .on('change:resolution', () => {
        // 当地图缩放级别变化时，重新绘制箭头
        if (this.scheduledPathFeature) {
          this.addArrowsToPath(
            this.scheduledPathFeature,
            MapLayerEnum.SCHEDULED_ROUTE_LAYER,
            this.scheduledPathArrows
          );
        }

        if (this.finishedPathFeature) {
          this.addArrowsToPath(
            this.finishedPathFeature,
            MapLayerEnum.FINISHED_ROUTE_LAYER,
            this.finishedPathArrows
          );
        }
      });
  }

  /**
   * 绘制起点
   * @param coordinates 坐标数组 [longitude, latitude]
   */
  drawStartPoint(coordinates: string[]) {
    const source =
      this.sourceMap[`${MapLayerEnum.START_REALTIME_POINT_LAYER}-source`];
    if (!this.getLayer(MapLayerEnum.START_REALTIME_POINT_LAYER)) {
      this.createLayer(MapLayerEnum.START_REALTIME_POINT_LAYER, { zIndex: 10 });
    }
    if (this.startPointFeature) {
      source?.removeFeature(this.startPointFeature);
    }
    const style = new Style({
      zIndex: 5,
      image: new Icon({
        src: require('@/assets/images/monitor/start.png'),
        anchor: [0.5, 0.5],
      }),
    });
    this.startPointFeature = this.createPointFeature(
      coordinates,
      style,
      'route-start-point'
    );
    this.addFeature(
      this.startPointFeature,
      MapLayerEnum.START_REALTIME_POINT_LAYER
    );
  }

  /**
   * 更新实时位置（红色标记）
   * @param coordinates 坐标数组 [longitude, latitude]
   * @param rotation 旋转角度
   */
  updateRealTimePosition(
    coordinates: number[],
    rotation?: number,
    isChangeCenter: boolean = true
  ) {
    if (!coordinates || coordinates.length !== 2) {
      return;
    }

    if (!this.getLayer(MapLayerEnum.START_REALTIME_POINT_LAYER)) {
      this.createLayer(MapLayerEnum.START_REALTIME_POINT_LAYER, { zIndex: 10 });
    }

    if (!this.realTimePositionFeature) {
      const style = new Style({
        zIndex: 10,
        image: new Icon({
          src: require('@/assets/images/monitor/vehicle-point.png'),
          anchor: [0.5, 0.5],
          scale: 0.3,
          rotation: rotation ? (rotation * Math.PI) / 180 : 0,
        }),
      });

      this.realTimePositionFeature = this.createPointFeature(
        coordinates,
        style,
        'vehicle-realTime-position'
      );

      this.addFeature(
        this.realTimePositionFeature,
        MapLayerEnum.START_REALTIME_POINT_LAYER
      );
    } else {
      this.realTimePositionFeature
        .getGeometry()
        .setCoordinates(this.transformCoordinate(coordinates));

      // 如果提供了旋转角度，更新图标旋转
      if (rotation !== undefined) {
        const style = this.realTimePositionFeature.getStyle();
        style.getImage().setRotation((rotation * Math.PI) / 180);
        this.realTimePositionFeature.setStyle(style);
      }
    }
    isChangeCenter && this.changeCenter(coordinates);
  }

  /**
   * 更新车辆信息气泡
   * @param coordinates 坐标
   * @param vehicleScheduleState 车辆调度状态
   * @param estimatedArrivalTime 预计到达时间
   * @param nextMileage 剩余里程
   */
  private updateVehicleInfoOverlay(
    coordinates: number[],
    vehicleScheduleState: string,
    estimatedArrivalTime?: string,
    nextMileage?: number
  ) {
    this.clearVehicleInfoOverlay();

    // 只有在去程或返程状态且有预计到达时间或剩余里程时才显示气泡
    if (
      (vehicleScheduleState === 'SETOUT' ||
        vehicleScheduleState === 'RETURN') &&
      (estimatedArrivalTime || nextMileage !== undefined)
    ) {
      // 创建气泡内容元素
      const element = document.createElement('div');
      element.className = 'vehicle-info-overlay';
      element.style.backgroundColor = 'white';
      element.style.color = '#3C6EF0';
      element.style.padding = '4px 8px';
      element.style.borderRadius = '4px';
      element.style.boxShadow = '0 2px 4px #525765';
      element.style.fontSize = '12px';
      element.style.whiteSpace = 'nowrap';
      element.style.pointerEvents = 'none';

      // 设置气泡内容
      let content = '';
      if (nextMileage !== undefined) {
        content += `${(nextMileage / 1000).toFixed(2)}km | `;
      }
      if (estimatedArrivalTime) {
        content += `预计<span style="font-weight: bold;">${estimatedArrivalTime}</span>到达`;
      }
      element.innerHTML = content;

      // 创建并添加overlay
      this.vehicleInfoOverlay = new Overlay({
        element: element,
        position: this.transformCoordinate(coordinates),
        positioning: 'bottom-center',
        offset: [0, -20], // 在车辆图标上方显示
        className: 'ol-vehicle-eta-overlay',
      });

      this.map.addOverlay(this.vehicleInfoOverlay);
    }
  }

  /**
   * 绘制停靠点或Home点List
   * @param points 停靠点或Home点列表
   */
  drawStopOrHomePoints(
    points: {
      type: string | null;
      id: number;
      name: string;
      lat: number | null;
      lon: number | null;
      travelStatus: string;
    }[]
  ) {
    // 清除现有的停靠点图层和overlays
    this.clearLayer(MapLayerEnum.POINTS_LAYER);
    this.stopPointOverlays = this.clearOverlays(
      this.stopPointOverlays,
      this.map
    );
    if (!Array.isArray(points) || points.length === 0) {
      return;
    }

    // 创建停靠点图层
    if (!this.getLayer(MapLayerEnum.POINTS_LAYER)) {
      this.createLayer(MapLayerEnum.POINTS_LAYER, { zIndex: 9 });
    }

    // 绘制每个点
    points.forEach((point, index) => {
      // 跳过无效坐标点
      if (point.lat === null || point.lon === null) {
        return;
      }

      const { type, id, name, lat, lon, travelStatus } = point;
      const coordinates = [lon, lat];

      // 根据类型选择图标
      let iconSrc;
      let scale = 1;
      if (type === 'HOME') {
        iconSrc = require('@/assets/images/singleVehicle/station-icon.png');
        scale = 0.5;
      } else {
        iconSrc = require('@/assets/images/singleVehicle/stop-icon.png');
        scale = 0.3;
      }

      // 创建点位样式
      const style = new Style({
        image: new Icon({
          src: iconSrc,
          anchor: [0.5, 0.5],
          scale: scale,
        }),
      });

      // 创建点位Feature
      const pointFeature = this.createPointFeature(
        coordinates,
        style,
        `stop-point-${id}`
      );

      // 添加到图层
      this.addFeature(pointFeature, MapLayerEnum.POINTS_LAYER);

      // 创建名称标签overlay
      const labelElement = document.createElement('div');
      labelElement.className = 'stop-point-label';
      labelElement.style.color = '#525765';
      labelElement.style.fontSize = '12px';
      labelElement.style.fontWeight = 'normal';
      labelElement.style.textAlign = 'center';
      labelElement.style.whiteSpace = 'nowrap';
      labelElement.style.pointerEvents = 'none';
      labelElement.style.backgroundColor = 'white';
      labelElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
      labelElement.style.padding = '2px 6px';
      labelElement.style.borderRadius = '4px';
      labelElement.innerHTML = name;

      // 创建并添加overlay
      const labelOverlay = new Overlay({
        element: labelElement,
        position: this.transformCoordinate(coordinates),
        positioning: 'bottom-center',
        offset: [0, -30], // 在图标上方显示
        className: 'ol-stop-overlay',
      });

      this.map.addOverlay(labelOverlay);
      this.stopPointOverlays.push(labelOverlay);
    });
  }

  /**
   * 清除停靠点
   */
  clearStopPoints() {
    this.clearLayer(MapLayerEnum.POINTS_LAYER);
    this.stopPointOverlays = this.clearOverlays(
      this.stopPointOverlays,
      this.map
    );
  }

  /**
   * 绘制车辆实时位置（与updateRealTimePosition功能类似，但用于Navigation组件）
   * @param coordinates 坐标数组 [longitude, latitude]
   * @param heading 车辆朝向角度
   */
  drawVehiclePosition(coordinates: number[], heading?: number) {
    if (!coordinates || coordinates.length !== 2) {
      return;
    }

    this.clearVehiclePosition();

    if (!this.getLayer(MapLayerEnum.START_REALTIME_POINT_LAYER)) {
      this.createLayer(MapLayerEnum.START_REALTIME_POINT_LAYER, {
        zIndex: 999,
      });
    }

    const style = new Style({
      image: new Icon({
        src: require('@/assets/images/monitor/vehicle-point.svg'),
        anchor: [0.5, 0.5],
        rotation: heading ? (heading * Math.PI) / 180 : 0,
      }),
    });

    this.vehiclePositionFeature = this.createPointFeature(
      coordinates,
      style,
      'vehicle-position'
    );

    this.addFeature(
      this.vehiclePositionFeature,
      MapLayerEnum.START_REALTIME_POINT_LAYER
    );

    return this.vehiclePositionFeature;
  }

  /**
   * 绘制调度路径
   * @param coordinates 坐标数组 [{latitude, longitude}, ...]
   */
  drawScheduledPath(coordinates: { latitude: number; longitude: number }[]) {
    if (!Array.isArray(coordinates) || coordinates.length === 0) {
      return;
    }
    const validCoordinates = coordinates.filter(
      (item) =>
        item &&
        typeof item?.longitude === 'number' &&
        typeof item?.latitude === 'number'
    );

    if (validCoordinates.length === 0) {
      return;
    }
    const list = validCoordinates.map((v) => [v.longitude, v.latitude]);

    if (!this.getLayer(MapLayerEnum.SCHEDULED_ROUTE_LAYER)) {
      this.createLayer(MapLayerEnum.SCHEDULED_ROUTE_LAYER, { zIndex: 8 });
    }
    if (!this.scheduledPathFeature) {
      const style = new Style({
        stroke: new Stroke({ color: '#AAAFBE', width: 8 }),
      });

      try {
        const lineGeometry = new LineString(this.transformCoordinates(list));
        this.scheduledPathFeature = new OlFeature({
          geometry: lineGeometry,
          name: 'scheduled-path',
        });
        this.scheduledPathFeature.setStyle(style);
        this.addFeature(
          this.scheduledPathFeature,
          MapLayerEnum.SCHEDULED_ROUTE_LAYER
        );
      } catch (error) {
        console.error('Error drawing scheduled path:', error);
      }
      // 添加箭头
      this.addArrowsToPath(
        this.scheduledPathFeature,
        MapLayerEnum.SCHEDULED_ROUTE_LAYER,
        this.scheduledPathArrows
      );
    } else {
      try {
        this.scheduledPathFeature
          .getGeometry()
          .setCoordinates(this.transformCoordinates(list));
        // 更新箭头
        this.addArrowsToPath(
          this.scheduledPathFeature,
          MapLayerEnum.SCHEDULED_ROUTE_LAYER,
          this.scheduledPathArrows
        );
      } catch (error) {
        console.error('Error updating scheduled path:', error);
      }
    }
  }

  /**
   * 绘制已完成路径
   * @param coordinates 坐标数组 [{latitude, longitude}, ...]
   */
  drawFinishedPath(coordinates: { latitude: number; longitude: number }[]) {
    if (!Array.isArray(coordinates) || coordinates.length === 0) {
      return;
    }

    const validCoordinates = coordinates.filter(
      (item) =>
        item &&
        typeof item?.longitude === 'number' &&
        typeof item?.latitude === 'number'
    );

    if (validCoordinates.length === 0) {
      return;
    }

    const list = validCoordinates.map((v) => [v.longitude, v.latitude]);

    if (!this.getLayer(MapLayerEnum.FINISHED_ROUTE_LAYER)) {
      this.createLayer(MapLayerEnum.FINISHED_ROUTE_LAYER, { zIndex: 9 });
    }

    if (!this.finishedPathFeature) {
      const style = new Style({
        stroke: new Stroke({
          color: '#53B066',
          width: 8,
        }),
      });

      try {
        const lineGeometry = new LineString(this.transformCoordinates(list));
        this.finishedPathFeature = new OlFeature({
          geometry: lineGeometry,
          name: 'finished-path',
        });
        this.finishedPathFeature.setStyle(style);
        this.addFeature(
          this.finishedPathFeature,
          MapLayerEnum.FINISHED_ROUTE_LAYER
        );
      } catch (error) {
        console.error('Error drawing finished path:', error);
      }
      // 添加箭头
      this.addArrowsToPath(
        this.finishedPathFeature,
        MapLayerEnum.FINISHED_ROUTE_LAYER,
        this.finishedPathArrows
      );
    } else {
      try {
        this.finishedPathFeature
          .getGeometry()
          .setCoordinates(this.transformCoordinates(list));
        // 更新箭头
        this.addArrowsToPath(
          this.finishedPathFeature,
          MapLayerEnum.FINISHED_ROUTE_LAYER,
          this.finishedPathArrows
        );
      } catch (error) {
        console.error('Error updating finished path:', error);
      }
    }
  }

  /**
   * 在路径上添加箭头标记
   * @param pathFeature 路径特性
   * @param layerEnum 图层枚举
   * @param pathColor 路径颜色（未使用，保留参数以兼容现有调用）
   * @param arrowsArray 箭头数组引用
   * @param spacing 箭头间距（米）
   */
  private addArrowsToPath(
    pathFeature: OlFeature,
    layerEnum: MapLayerEnum,
    arrowsArray: OlFeature[],
    spacing: number = 200
  ) {
    // 清除现有箭头
    this.clearArrows(arrowsArray, layerEnum);

    if (!pathFeature) return;

    try {
      // 获取路径几何
      const lineGeometry = pathFeature.getGeometry() as LineString;
      if (!lineGeometry) return;
      const coordinates = lineGeometry.getCoordinates();
      if (coordinates.length < 2) return;

      // 判断是否平面投影
      let lineLength = 0;
      const view = this.map.getView();
      const isWebMercator = view?.getProjection()?.getCode() === 'EPSG:3857';

      if (isWebMercator) {
        // 如果是平面投影，直接取线路geo的长度
        lineLength = lineGeometry.getLength();
      } else {
        // 非平面投影，取路径长度
        const line = turf.lineString(coordinates);
        // 获取路径长度
        lineLength = turf.length(line) * 1000;
      }

      // 根据地图层级动态调整箭头间距
      const mapView = this.map.getView();
      const zoom = mapView.getZoom();
      let adjustedSpacing = spacing;
      if (zoom !== undefined) {
        const baseZoom = 15;
        const scaleFactor = Math.pow(0.85, zoom - baseZoom);
        adjustedSpacing = spacing * scaleFactor;
        adjustedSpacing = Math.max(30, Math.min(adjustedSpacing, 500));
      }

      let numArrows = 0;
      if (lineLength > 1000) {
        numArrows = Math.floor(lineLength / adjustedSpacing);
      } else if (lineLength > 500) {
        numArrows = Math.floor(lineLength / (adjustedSpacing * 0.5));
      } else if (lineLength > 200) {
        numArrows = Math.floor(lineLength / (adjustedSpacing * 0.25));
      } else if (lineLength > 100) {
        numArrows = Math.floor(lineLength / (adjustedSpacing * 0.125));
      } else {
        numArrows = 5;
      }

      // 限制最大箭头数量，避免过多箭头
      const maxArrows = 20;
      const actualNumArrows = Math.min(numArrows, maxArrows);

      // 在路径上均匀分布箭头
      for (let i = 1; i < actualNumArrows; i++) {
        // 计算箭头位置
        const fraction = i / actualNumArrows;

        // 获取箭头位置
        const arrowCoord = lineGeometry.getCoordinateAt(fraction);

        // 计算箭头方向（获取前后点计算角度）
        const prevFraction = Math.max(0, fraction - 0.01);
        const nextFraction = Math.min(1, fraction + 0.01);
        const prevCoord = lineGeometry.getCoordinateAt(prevFraction);
        const nextCoord = lineGeometry.getCoordinateAt(nextFraction);

        const dx = nextCoord[0] - prevCoord[0];
        const dy = nextCoord[1] - prevCoord[1];
        const rotation = Math.atan2(dy, dx);
        const arrowAngle = rotation - Math.PI / 2;

        // 创建箭头图标样式
        const arrowStyle = new Style({
          image: new Icon({
            src: require('@/assets/images/monitor/arrow.svg'),
            anchor: [0.5, 0.5],
            scale: 0.5,
            rotateWithView: true,
            rotation: -arrowAngle,
          }),
        });

        // 创建箭头特性
        const arrowFeature = new OlFeature({
          geometry: new Point(arrowCoord),
          name: `arrow-${layerEnum}-${i}`,
        });

        arrowFeature.setStyle(arrowStyle);
        this.addFeature(arrowFeature, layerEnum);

        // 将箭头添加到数组中，以便后续清除
        arrowsArray.push(arrowFeature);
      }
    } catch (error) {
      console.error('Error adding arrows to path:', error);
    }
  }
  /**
   * 绘制/更新轨迹
   * @param coordinates 坐标数组 [[{latitude, longitude}, ...], ...]
   */
  updateTravelPath(coordinates: { latitude: number; longitude: number }[][]) {
    if (!Array.isArray(coordinates) || coordinates.length === 0) {
      return;
    }

    const validCoordinates = coordinates.filter(
      (arr) =>
        Array.isArray(arr) &&
        arr.length > 0 &&
        arr.every(
          (item) =>
            item &&
            typeof item?.longitude === 'number' &&
            typeof item?.latitude === 'number'
        )
    );

    if (validCoordinates.length === 0) {
      return;
    }

    const list = validCoordinates.map((v) =>
      v.map((i) => [i.longitude, i.latitude])
    );
    if (!this.getLayer(MapLayerEnum.TRAVEL_ROUTE_LAYER)) {
      this.createLayer(MapLayerEnum.TRAVEL_ROUTE_LAYER, { zIndex: 7 });
    }

    if (!this.travelPathFeature) {
      const style = new Style({
        stroke: new Stroke({
          color: '#00CC6D',
          width: 3,
        }),
      });

      try {
        const multiLineGeometry = new MultiLineString(
          list.map((l: any) => this.transformCoordinates(l))
        );
        this.travelPathFeature = new OlFeature({
          geometry: multiLineGeometry,
          name: 'travel-path',
        });

        this.travelPathFeature.setStyle(style);
        this.addFeature(
          this.travelPathFeature,
          MapLayerEnum.TRAVEL_ROUTE_LAYER
        );
      } catch (error) {
        console.error('Error drawing travel path:', error);
      }
    } else {
      try {
        this.travelPathFeature
          .getGeometry()
          .setCoordinates(list.map((l: any) => this.transformCoordinates(l)));
      } catch (error) {
        console.error('Error updating travel path:', error);
      }
    }
  }

  /**
   * 自定义路线规划（用于MapRoutesOperateWithCustomRouting）
   * @param coordinates OpenLayers格式的坐标数组 [[longitude, latitude], ...]
   */
  customRouting(coordinates: number[][]) {
    if (!Array.isArray(coordinates) || coordinates.length === 0) {
      this.clearCustomRouting();
      return;
    }

    this.clearCustomRouting();

    // 创建路线图层
    if (!this.getLayer(MapLayerEnum.SCHEDULED_ROUTE_LAYER)) {
      this.createLayer(MapLayerEnum.SCHEDULED_ROUTE_LAYER, { zIndex: 10 });
    }

    // 创建起点终点图层
    if (!this.getLayer(MapLayerEnum.POINTS_LAYER)) {
      this.createLayer(MapLayerEnum.POINTS_LAYER, { zIndex: 11 });
    }

    // 绘制路线
    const style = new Style({
      stroke: new Stroke({
        color: '#0D85FF',
        width: 3,
      }),
    });

    try {
      const lineGeometry = new LineString(
        this.transformCoordinates(coordinates)
      );
      this.customRoutingFeature = new OlFeature({
        geometry: lineGeometry,
        name: 'custom-routing-path',
      });

      this.customRoutingFeature.setStyle(style);
      this.addFeature(
        this.customRoutingFeature,
        MapLayerEnum.SCHEDULED_ROUTE_LAYER
      );

      // 绘制起点
      const startStyle = new Style({
        image: new Icon({
          src: require('@/assets/images/monitor/start.png'),
          anchor: [0.5, 0.5],
          scale: 0.5,
        }),
      });

      this.startPointFeature = this.createPointFeature(
        coordinates[0],
        startStyle,
        'custom-routing-start'
      );

      this.addFeature(this.startPointFeature, MapLayerEnum.POINTS_LAYER);

      // 绘制终点
      const endStyle = new Style({
        image: new Icon({
          src: require('@/assets/images/monitor/end.png'),
          anchor: [0.5, 0.5],
          scale: 0.5,
        }),
      });

      this.endPointFeature = this.createPointFeature(
        coordinates[coordinates.length - 1],
        endStyle,
        'custom-routing-end'
      );

      this.addFeature(this.endPointFeature, MapLayerEnum.POINTS_LAYER);

      // 将地图中心设置为路线起点
      this.changeCenter(coordinates[0]);
    } catch (error) {
      console.error('Error drawing custom routing:', error);
    }
  }

  /**
   * 绘制路线规划路径（用于NavigationOperate）
   * @param coordinates 路径坐标数组
   */
  drawRoutingPath(coordinates: any[]) {
    if (!Array.isArray(coordinates) || coordinates.length === 0) {
      this.clearRoutingPath();
      return;
    }

    this.clearRoutingPath();

    // 将QQ地图的LatLng对象转换为OpenLayers坐标数组 [longitude, latitude]
    const list = coordinates.map((point) => {
      if (point.lat !== undefined && point.lng !== undefined) {
        return [point.lng, point.lat];
      } else {
        return point;
      }
    });

    if (!this.getLayer(MapLayerEnum.SCHEDULED_ROUTE_LAYER)) {
      this.createLayer(MapLayerEnum.SCHEDULED_ROUTE_LAYER, { zIndex: 999 });
    }

    const style = new Style({
      stroke: new Stroke({
        color: '#3777FF',
        width: 8,
      }),
    });

    try {
      const lineGeometry = new LineString(this.transformCoordinates(list));
      this.routingPathFeature = new OlFeature({
        geometry: lineGeometry,
        name: 'routing-path',
      });

      this.routingPathFeature.setStyle(style);
      this.addFeature(
        this.routingPathFeature,
        MapLayerEnum.SCHEDULED_ROUTE_LAYER
      );

      // 绘制起点和终点标记
      this.drawPoiMarker(list[0], require('@/assets/images/monitor/start.png'));
      this.drawPoiMarker(
        list[list.length - 1],
        require('@/assets/images/monitor/end.png')
      );

      // 将地图中心设置为路线起点
      this.changeCenter(list[0]);
    } catch (error) {
      console.error('Error drawing routing path:', error);
    }

    return list;
  }

  /**
   * 绘制POI标记点（用于NavigationOperate）
   * @param coordinates 坐标 [longitude, latitude]
   * @param iconSrc 图标路径
   */
  drawPoiMarker(coordinates: number[], iconSrc: string) {
    if (!coordinates || coordinates.length !== 2) {
      return;
    }

    if (!this.getLayer(MapLayerEnum.POINTS_LAYER)) {
      this.createLayer(MapLayerEnum.POINTS_LAYER, { zIndex: 999 });
    }

    const style = new Style({
      image: new Icon({
        src: iconSrc,
        anchor: [0.5, 0.5],
        scale: 0.5,
      }),
    });

    const poiFeature = this.createPointFeature(
      coordinates,
      style,
      `poi-marker-${Date.now()}`
    );

    this.addFeature(poiFeature, MapLayerEnum.POINTS_LAYER);
    this.poiMarkers.push(poiFeature);

    return poiFeature;
  }

  /**
   * 绘制采图路线（用于CollectMapOperate）
   * @param coordinates 路线坐标数组
   */
  drawTaskRoutePath(coordinates: { latitude: number; longitude: number }[]) {
    if (!Array.isArray(coordinates) || coordinates.length === 0) {
      this.clearLayer(MapLayerEnum.PLANNED_ROUTE_LAYER);
      return;
    }

    const list = coordinates.map((v) => [v.longitude, v.latitude]);

    if (!this.getLayer(MapLayerEnum.PLANNED_ROUTE_LAYER)) {
      this.createLayer(MapLayerEnum.PLANNED_ROUTE_LAYER, { zIndex: 5 });
    }

    if (!this.taskRouteFeature) {
      const style = new Style({
        stroke: new Stroke({
          color: '#0CC5CF',
          width: 6,
        }),
      });

      try {
        const lineGeometry = new LineString(this.transformCoordinates(list));
        this.taskRouteFeature = new OlFeature({
          geometry: lineGeometry,
          name: 'task-route-path',
        });

        this.taskRouteFeature.setStyle(style);
        this.addFeature(
          this.taskRouteFeature,
          MapLayerEnum.PLANNED_ROUTE_LAYER
        );
      } catch (error) {
        console.error('Error drawing task route path:', error);
      }
    } else {
      try {
        this.taskRouteFeature
          .getGeometry()
          .setCoordinates(this.transformCoordinates(list));
      } catch (error) {
        console.error('Error updating task route path:', error);
      }
    }
  }

  /**
   * 绘制四车道（用于CollectMapOperate）
   * @param taskRouteList 任务路线列表
   * @param fourLaneIndexList 四车道索引列表
   */
  drawFourLanePath(
    taskRouteList: { latitude: number; longitude: number }[],
    fourLaneIndexList: { routeIndexList: number[] }[]
  ) {
    if (
      !taskRouteList ||
      !fourLaneIndexList ||
      fourLaneIndexList.length === 0
    ) {
      this.clearLayer(MapLayerEnum.TRAVEL_ROUTE_LAYER);
      this.fourLaneFeatures = [];
      return;
    }

    this.clearLayer(MapLayerEnum.TRAVEL_ROUTE_LAYER);
    this.fourLaneFeatures = [];

    if (!this.getLayer(MapLayerEnum.TRAVEL_ROUTE_LAYER)) {
      this.createLayer(MapLayerEnum.TRAVEL_ROUTE_LAYER, { zIndex: 12 });
    }

    fourLaneIndexList.forEach((fourLaneObj) => {
      const fourLaneCoordinates = fourLaneObj.routeIndexList.map(
        (routeIndex) => [
          taskRouteList[routeIndex].longitude,
          taskRouteList[routeIndex].latitude,
        ]
      );

      const style = new Style({
        stroke: new Stroke({
          color: '#FFAA00',
          width: 6,
        }),
      });

      try {
        const lineGeometry = new LineString(
          this.transformCoordinates(fourLaneCoordinates)
        );
        const fourLaneFeature = new OlFeature({
          geometry: lineGeometry,
          name: 'four-lane-path',
        });

        fourLaneFeature.setStyle(style);
        this.addFeature(fourLaneFeature, MapLayerEnum.TRAVEL_ROUTE_LAYER);
        this.fourLaneFeatures.push(fourLaneFeature);
      } catch (error) {
        console.error('Error drawing four lane path:', error);
      }
    });
  }

  /**
   * 绘制起点和终点（用于CollectMapOperate）
   * @param taskRouteList 任务路线列表
   */
  drawStartAndEnd(taskRouteList: { latitude: number; longitude: number }[]) {
    if (!taskRouteList || taskRouteList.length === 0) {
      this.clearStartEndMarkers();
      return;
    }

    this.clearStartEndMarkers();

    const startCoordinates = [
      taskRouteList[0].longitude,
      taskRouteList[0].latitude,
    ];

    const endCoordinates = [
      taskRouteList[taskRouteList.length - 1].longitude,
      taskRouteList[taskRouteList.length - 1].latitude,
    ];

    if (!this.getLayer(MapLayerEnum.POINTS_LAYER)) {
      this.createLayer(MapLayerEnum.POINTS_LAYER, { zIndex: 14 });
    }

    // 起点样式
    const startStyle = new Style({
      zIndex: 14,
      image: new Icon({
        src: require('@/assets/images/collectMap/start-point-icon.png'),
        anchor: [0.5, 1],
        scale: 0.5,
      }),
    });

    // 终点样式
    const endStyle = new Style({
      zIndex: 14,
      image: new Icon({
        src: require('@/assets/images/collectMap/end-point-icon.png'),
        anchor: [0.5, 1],
        scale: 0.5,
      }),
    });

    this.startPointFeature = this.createPointFeature(
      startCoordinates,
      startStyle,
      'start-point'
    );

    this.endPointFeature = this.createPointFeature(
      endCoordinates,
      endStyle,
      'end-point'
    );

    this.addFeature(this.startPointFeature, MapLayerEnum.POINTS_LAYER);
    this.addFeature(this.endPointFeature, MapLayerEnum.POINTS_LAYER);
  }
  /**
   * 清除路线规划路径
   */
  clearRoutingPath() {
    if (this.routingPathFeature) {
      const source = this.getLayer(
        MapLayerEnum.SCHEDULED_ROUTE_LAYER
      )?.getSource();
      if (source) {
        source.removeFeature(this.routingPathFeature);
      }
      this.routingPathFeature = null;
    }
  }

  /**
   * 清除POI标记点
   */
  clearPoiMarkers() {
    if (this.poiMarkers.length > 0) {
      const source = this.getLayer(MapLayerEnum.POINTS_LAYER)?.getSource();
      if (source) {
        this.poiMarkers.forEach((marker) => {
          source.removeFeature(marker);
        });
      }
      this.poiMarkers = [];
    }
  }

  /**
   * 清除车辆位置标记
   */
  clearVehiclePosition() {
    if (this.vehiclePositionFeature) {
      const source = this.getLayer(
        MapLayerEnum.START_REALTIME_POINT_LAYER
      )?.getSource();
      if (source) {
        source.removeFeature(this.vehiclePositionFeature);
      }
      this.vehiclePositionFeature = null;
    }
  }

  /**
   * 清除箭头
   * @param arrowsArray 箭头数组
   * @param layerEnum 图层枚举，用于确定箭头所在图层
   */
  private clearArrows(arrowsArray: OlFeature[], layerEnum: MapLayerEnum) {
    // 使用基类的通用清除方法
    this.clearFeatures(arrowsArray, layerEnum);
    arrowsArray.length = 0;
  }

  /**
   * 清除自定义路线规划
   */
  clearCustomRouting() {
    // 清除路线
    if (this.customRoutingFeature) {
      const source = this.getLayer(
        MapLayerEnum.SCHEDULED_ROUTE_LAYER
      )?.getSource();
      if (source) {
        source.removeFeature(this.customRoutingFeature);
      }
      this.customRoutingFeature = null;
    }

    // 清除起点和终点标记
    this.clearStartEndMarkers();
  }

  /**
   * 清除起点和终点标记
   */
  clearStartEndMarkers() {
    if (this.startPointFeature) {
      const source = this.getLayer(MapLayerEnum.POINTS_LAYER)?.getSource();
      if (source) {
        source.removeFeature(this.startPointFeature);
      }
      this.startPointFeature = null;
    }

    if (this.endPointFeature) {
      const source = this.getLayer(MapLayerEnum.POINTS_LAYER)?.getSource();
      if (source) {
        source.removeFeature(this.endPointFeature);
      }
      this.endPointFeature = null;
    }
  }

  /**
   * 绘制特殊标记（路口/豁口）
   * @param specialMarkList 特殊标记列表
   */
  drawSpecialMarkers(specialMarkList: any[]) {
    if (!specialMarkList || specialMarkList.length === 0) {
      this.clearSpecialMarkers();
      return;
    }

    this.clearSpecialMarkers();

    if (!this.getLayer(MapLayerEnum.POINTS_LAYER)) {
      this.createLayer(MapLayerEnum.POINTS_LAYER, { zIndex: 13 });
    }

    specialMarkList.forEach((specialMark) => {
      const coordinates = [specialMark.longitude, specialMark.latitude];

      // 根据标记类型选择图标
      const iconSrc =
        specialMark.markType === 'ROAD_GAP'
          ? require('@/assets/images/collectMap/gap-icon.png')
          : require('@/assets/images/collectMap/intersection-icon.png');

      const style = new Style({
        zIndex: 13,
        image: new Icon({
          src: iconSrc,
          anchor: [0.5, 1],
          scale: 0.5,
        }),
      });

      const feature = this.createPointFeature(
        coordinates,
        style,
        `special-marker-${specialMark.markId}`
      );

      // 添加点击事件
      const clickKey = this.map.on('click', (evt: any) => {
        const pixel = this.map.getEventPixel(evt.originalEvent);
        const hit = this.map.hasFeatureAtPixel(pixel, {
          layerFilter: (layer: any) => {
            return layer.get('layerId') === MapLayerEnum.POINTS_LAYER;
          },
        });

        if (hit) {
          const features = this.map.getFeaturesAtPixel(pixel, {
            layerFilter: (layer: any) => {
              return layer.get('layerId') === MapLayerEnum.POINTS_LAYER;
            },
          });

          if (features && features.includes(feature)) {
            this.showSpecialMarkerOverlay(specialMark, coordinates);
          }
        }
      });

      // 保存点击事件key，后续clear时清除
      this._specialMarkerClickKeys.push(clickKey);

      this.addFeature(feature, MapLayerEnum.POINTS_LAYER);
      this.specialMarkers.push(feature);
    });
  }
  /**
   * 清除特殊标记弹出层
   */
  clearSpecialOverlays() {
    this.specialOverlays.forEach((overlay) => {
      this.map.removeOverlay(overlay);
    });
    this.specialOverlays = [];
  }
  /**
   * 显示特殊标记的弹出层
   * @param specialMark 特殊标记数据
   * @param coordinates 坐标 [longitude, latitude]
   */
  showSpecialMarkerOverlay(specialMark: any, coordinates: number[]) {
    // 清除之前的弹出层
    this.clearSpecialOverlays();

    let width = 330;
    let height = 100;
    let imageListDom = '';

    const attachmentList = specialMark.attachmentList || [];

    switch (attachmentList.length) {
      case 1:
        width = 330;
        height = 400;
        imageListDom = `<img class='image-item' id=${attachmentList[0].fileKey} src=${attachmentList[0].url} ></img>`;
        break;
      case 2:
        width = 500;
        height = 400;
        imageListDom =
          `<img class='image-item' id=${attachmentList[0].fileKey} src=${attachmentList[0].url} ></img>` +
          `<img class='image-item' id=${attachmentList[1].fileKey} src=${attachmentList[1].url} ></img>`;
        break;
      case 3:
        width = 600;
        height = 400;
        imageListDom =
          `<img class='image-item' id=${attachmentList[0].fileKey} src=${attachmentList[0].url} ></img>` +
          `<img class='image-item' id=${attachmentList[1].fileKey} src=${attachmentList[1].url} ></img>` +
          `<img class='image-item' id=${attachmentList[2].fileKey} src=${attachmentList[2].url} ></img>`;
        break;
      default:
        width = 330;
        height = 100;
        break;
    }

    const overlayElement = document.createElement('div');
    overlayElement.innerHTML =
      '<div class="overlay-container"' +
      `style='width: ${width}px; height:${height}px'>` +
      `<div class="label-text"><div class='label'>备注:</div><div class='text'>${
        specialMark.remark ?? ''
      }</div></div>` +
      '<div class="image-list">' +
      imageListDom +
      '</div>' +
      '<div class="triangle">' +
      '</div>' +
      '</div>';
    const offsetTop = height > 100 ? height - 350 : height - 50;
    const overlay = new Overlay({
      element: overlayElement,
      position: this.transformCoordinate(coordinates),
      positioning: 'bottom-center',
      offset: [0, -offsetTop],
    });

    this.map.addOverlay(overlay);
    this.specialOverlays.push(overlay);

    // 10秒后自动关闭
    setTimeout(() => {
      this.map.removeOverlay(overlay);
      const index = this.specialOverlays.indexOf(overlay);
      if (index > -1) {
        this.specialOverlays.splice(index, 1);
      }
    }, 10000);
  }

  /**
   * 清除特殊标记
   */
  clearSpecialMarkers() {
    // 移除特殊标记的点击事件
    this._specialMarkerClickKeys.forEach((key) => unByKey(key));
    this._specialMarkerClickKeys = [];
    if (this.specialMarkers.length > 0) {
      const source = this.getLayer(MapLayerEnum.POINTS_LAYER)?.getSource();
      if (source) {
        this.specialMarkers.forEach((marker) => {
          source.removeFeature(marker);
        });
      }
      this.specialMarkers = [];
    }
  }

  /**
   * 清除所有标记和路径
   */
  clearAll() {
    // 清除路线
    this.clearLayer(MapLayerEnum.SCHEDULED_ROUTE_LAYER);
    this.clearLayer(MapLayerEnum.FINISHED_ROUTE_LAYER);
    this.clearLayer(MapLayerEnum.TRAVEL_ROUTE_LAYER);
    this.clearLayer(MapLayerEnum.PLANNED_ROUTE_LAYER);

    // 清除点标记
    this.clearLayer(MapLayerEnum.POINTS_LAYER);
    this.clearLayer(MapLayerEnum.START_REALTIME_POINT_LAYER);

    // 清除特殊标记
    this.clearSpecialMarkers();

    // 重置所有特性
    this.realTimePositionFeature = null;
    this.vehiclePositionFeature = null;
    this.startPointFeature = null;
    this.endPointFeature = null;
    this.travelPathFeature = null;
    this.scheduledPathFeature = null;
    this.finishedPathFeature = null;
    this.routingPathFeature = null;
    this.customRoutingFeature = null;
    this.taskRouteFeature = null;
    this.poiMarkers = [];
    this.fourLaneFeatures = [];
    this.specialMarkers = [];

    // 清除箭头
    this.clearArrows(
      this.scheduledPathArrows,
      MapLayerEnum.SCHEDULED_ROUTE_LAYER
    );
    this.clearArrows(
      this.finishedPathArrows,
      MapLayerEnum.FINISHED_ROUTE_LAYER
    );
    // 清除覆盖物
    if (this.specialOverlays && this.specialOverlays.length > 0) {
      this.specialOverlays.forEach((overlay) => {
        this.map.removeOverlay(overlay);
      });
      this.specialOverlays = [];
    }

    // 移除地图底图的change:resolution的监听事件
    if (this._resolutionListenerKey !== null) {
      unByKey(this._resolutionListenerKey);
      this._resolutionListenerKey = null;
    }

    // 清理sourceMap来修复内存泄漏
    this.sourceMap = {};
  }

  /**
   * 更新实时位置（支持车辆信息气泡）
   * @param coordinates 坐标数组 [longitude, latitude]
   * @param rotation 旋转角度
   * @param params 车辆信息参数
   * @param params.vehicleScheduleState 车辆调度状态
   * @param params.estimatedArrivalTime 预计到达时间
   * @param params.nextMileage 剩余里程
   */
  updateRealTimePositionWithInfo(
    coordinates: number[],
    rotation?: number,
    params?: {
      vehicleScheduleState?: string;
      estimatedArrivalTime?: string;
      nextMileage?: number;
    }
  ) {
    // 先调用原有的更新位置方法
    this.updateRealTimePosition(coordinates, rotation, false);
    // 如果提供了车辆信息参数，则更新车辆信息气泡
    if (params) {
      const { vehicleScheduleState, estimatedArrivalTime, nextMileage } =
        params;
      if (vehicleScheduleState) {
        this.updateVehicleInfoOverlay(
          coordinates,
          vehicleScheduleState,
          estimatedArrivalTime,
          nextMileage
        );
      }
    }
  }

  /**
   * 清除车辆信息气泡
   */
  clearVehicleInfoOverlay() {
    if (this.vehicleInfoOverlay) {
      this.map.removeOverlay(this.vehicleInfoOverlay);
      this.vehicleInfoOverlay = null;
    }
  }

  /**
   * 清除调度路径（规划中、灰色路径）
   */
  public clearScheduledPath() {
    if (this.scheduledPathFeature) {
      const source = this.getLayer(
        MapLayerEnum.SCHEDULED_ROUTE_LAYER
      )?.getSource();
      if (source) {
        try {
          source.removeFeature(this.scheduledPathFeature);
        } catch (e) {
          console.error('Failed to remove scheduledPathFeature:', e);
        }
      }
      this.scheduledPathFeature = null;
    }
    this.clearArrows(
      this.scheduledPathArrows,
      MapLayerEnum.SCHEDULED_ROUTE_LAYER
    );
  }

  /**
   * 清除已完成路径（绿色路径）
   */
  public clearFinishedPath() {
    if (this.finishedPathFeature) {
      const source = this.getLayer(
        MapLayerEnum.FINISHED_ROUTE_LAYER
      )?.getSource();
      if (source) {
        try {
          source.removeFeature(this.finishedPathFeature);
        } catch (e) {
          console.error('Failed to remove finishedPathFeature:', e);
        }
      }
      this.finishedPathFeature = null;
    }
    this.clearArrows(
      this.finishedPathArrows,
      MapLayerEnum.FINISHED_ROUTE_LAYER
    );
  }
}

export default MapOperations;
